import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, Segment } from 'semantic-ui-react';
import Alert from '../../../../../utils/alert';
import ImportsRepo from '../../../../../utils/repository/importsRepo';
import useModalState from '../../../../../hooks/useModalState';
import {
    DescriptorCleanedLabel,
    DescriptorsMapType,
    DescriptorsToLabelsMapType,
    ExtractionType,
    FieldValueUpdateSpec,
    ImpactResponseType,
    ImpactType,
    ImportTableType,
    ReportsMapType,
    RowIdType,
} from '../../types';
import ApplyConfirmationModal from './components/applyConfirmationModal';
import LabelNormalizationModal from './components/labelNormalizationModal';
import Legend from './components/legend';
import Table from './components/table';
import { DEFAULT_IMPACT_VALUES, EVENTS } from './constants';
import {
    useImportTableStoreDescriptorsEditMode,
    useImportTableStoreReportsEditMode,
    useImportTableStoreDescriptorActions,
    useImportTableStoreDescriptorsMap,
    useImportTableStoreReportActions,
    useImportTableStoreReportsErrors,
    useImportTableStoreReportsMap,
    useImportTableStoreCoordinatesActions,
    useImportTableStoreNavigationActiveCoordinates,
} from './context/importTableContext';
import ImpactModal from './components/impactModal';
import { KEYS, RESPONSE_STATUS } from '../../../../../constnats/general';
import { IMPORT_DESCRIPTOR_SOURCE } from '../../constants/generalConstants';
import { getCleanedLabels } from './components/labelNormalizationModal/utils';
import BrowserStorage from '../../../../../utils/browserStorage';
import { debounce } from 'lodash';
import { getReportsState } from './store/importTableStore/utils';
import useFullModalState from '../../../../../hooks/useFullModalState';
import FieldModal from '../../../company/components/financialTables/schema/descriptors/fieldModal';
import { NO_OP_CALC } from '../../../../../constnats/schemaConstants';
import { DescriptorProps } from '../../../../../constnats/descriptor';
import { FormSection, TextFiedSpec } from '../../../../../utils/htmlFieldsFactory';
import {
    getCalcFieldCfgSpec,
    primaryCalcDisabled,
    validatePrimaryCalc,
} from '../../../company/components/financialTables/schema/descriptors/utils/fieldModalUtils';
import './importTable.css';
import { getDescriptorsUpdateSpec } from './utils';

const FORM_SPEC = {
    fields: [
        new TextFiedSpec(DescriptorProps.LABEL, {
            customStaticProps: {
                disabled: true,
            },
        }),
        new FormSection('Vertical Calculations', [
            getCalcFieldCfgSpec(
                DescriptorProps.CALCULATION,
                'Primary Calculation',
                primaryCalcDisabled,
                validatePrimaryCalc
            ),
        ]),
    ],
};

const RECALCULATE_WAIT_TIME = 700;

const debouncedRecalculateApiCall = debounce(recalculateTable, RECALCULATE_WAIT_TIME);

type ImportTablePropsType = {
    extraction: ExtractionType;
    setImportTable: (importTable: ImportTableType) => void;
    handleDeleteImport: () => void;
    redirectBack: () => void;
};
const ImportTable = ({ extraction, setImportTable, handleDeleteImport, redirectBack }: ImportTablePropsType) => {
    const labelNormalizationModalState = useModalState();
    const fieldModalState = useFullModalState<{ calculation: string; type: string; id: string; label: string }>();
    const labelNormalizationWithSave = useModalState();
    const applyModalState = useModalState();
    const impactModalState = useModalState();
    const [loading, setLoading] = useState(false);
    const [impact, setImpact] = useState<ImpactType>(DEFAULT_IMPACT_VALUES);
    const { activeRow, activeCol } = useImportTableStoreNavigationActiveCoordinates();
    const { setCoordinates: setCellCoordinates } = useImportTableStoreCoordinatesActions();
    const {
        cancelDescriptorsEditing,
        setDescriptorsLabels,
        disableDescriptorsEditMode,
        updateDescriptor,
        getNormalizationCandidates,
    } = useImportTableStoreDescriptorActions();
    const { cancelAllReportsEditing, disableAllReportsEditMode, setReportsMap } = useImportTableStoreReportActions();
    const reportsMap = useImportTableStoreReportsMap();
    const descriptorsMap = useImportTableStoreDescriptorsMap();
    const descriptorsEditMode = useImportTableStoreDescriptorsEditMode();
    const reportsEditMode = useImportTableStoreReportsEditMode();
    const reportsErrors = useImportTableStoreReportsErrors();
    const [imagePopupOpen, setImagePopupOpen] = useState(false);
    const [hasPendingNormalization, setHasPendingNormalization] = useState(false);
    const fieldModalDescriptors = Object.values(descriptorsMap).map((x) => ({
        ...x,
        type: x.dataType.toLowerCase(),
        dynamicField: true,
        calculation: x.verticalCalculation,
        secondaryCalculation: NO_OP_CALC,
        subFields: [],
    }));

    const processingRequestTS = useRef<number | null>(null);

    const onRecalculationError = useCallback((error: any) => {

        if (processingRequestTS.current === error.requestTimeStamp) {
            debouncedRecalculateApiCall.cancel();
            Alert.warn(`Recalculation failed with message: ${JSON.stringify(error)}`)
        }

    }, []);

    const onRecalculationComplete = useCallback(
        (result: any) => {
            if (processingRequestTS.current === result.requestTimeStamp) {
                if (result.status === RESPONSE_STATUS.SUCCESS &&
                    (descriptorsEditMode || reportsEditMode)) {
                    setReportsMap(getReportsState(result.data.reports, { editing: reportsEditMode }).reportsMap);
                } else {
                    onRecalculationError(result);
                }
            }
        },
        [extraction.id, descriptorsEditMode, reportsEditMode]
    );

    const recalculate = useCallback(
        (reportsMap: ReportsMapType, descriptorsMap: DescriptorsMapType) => {
            processingRequestTS.current = Date.now()

            debouncedRecalculateApiCall(
                processingRequestTS.current,
                extraction.id,
                descriptorsMap,
                reportsMap,
                onRecalculationComplete,
                onRecalculationError,
            );
        },
        [extraction.id, onRecalculationComplete]
    );

    // Cleanup
    useEffect(() => {
        return () => {
            debouncedRecalculateApiCall.cancel();
        };
    }, []);

    const normalizationCandidate = getNormalizationCandidates();
    const hasLabelsToNormalize = useMemo(
        () => !!getCleanedLabels(normalizationCandidate).length,
        [normalizationCandidate]
    );

    const haveLabelsBeenNormalized = BrowserStorage.getLabelsNormalizationStatus(extraction.id);

    const requiresLabelsNormalization = hasLabelsToNormalize && !haveLabelsBeenNormalized && !hasPendingNormalization;

    const handleCheckImpact = () => {
        ImportsRepo.checkImpact(extraction.id, (response: ImpactResponseType) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                setImpact(response.data);
            } else {
                Alert.error(`ERROR: ${response.message}`);
            }
        });
    };

    const handleOpenImpactModal = () => {
        handleCheckImpact();
        impactModalState.openModal();
    };

    const handleOpenApplyModal = () => {
        if (descriptorsEditMode || reportsEditMode) {
            Alert.warn('You have unsaved changes. Please either save or cancel the changes before applying!');
            return;
        }

        if (requiresLabelsNormalization) {
            labelNormalizationWithSave.openModal();
        } else {
            handleCheckImpact();
            applyModalState.openModal();
        }
    };

    const handleSaveReports = useCallback(() => {
        setLoading(true);
        let hasErrors = false;

        for (const reportErrors of Object.values(reportsErrors)) {
            const hasReportErrors = Object.values(reportErrors).some((x) => x.error);
            if (hasReportErrors) {
                hasErrors = true;
                break;
            }
        }

        if (hasErrors) {
            Alert.warn('Cannot submit changes while incorrect values are present!');
            setLoading(false);
            return;
        }

        const reportsToSave = Object.values(reportsMap).map((report) => {
            return {
                id: report.id,
                rowIdToValue: Object.entries(reportsMap[report.id].rowIdToValue).reduce(
                    (acc: Record<RowIdType, FieldValueUpdateSpec>, [key, value]) => {
                        acc[key] = {
                            value: value.normalizedValue,
                            manuallyDisabledVerticalCalc: value.manuallyDisabledVerticalCalc,
                        };
                        return acc;
                    },
                    {}
                ),
            };
        });

        ImportsRepo.reportsBatchUpdate(extraction.id, { reportUpdates: reportsToSave }, (result: any) => {
            if (result.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Reports updated successfully');
                disableAllReportsEditMode();
                setImportTable(result.data);
            } else {
                Alert.error(`ERROR: ${result.message}`);
            }
            setLoading(false);
        });
    }, [reportsMap, setLoading, disableAllReportsEditMode, setImportTable, extraction.id, reportsErrors]);

    const handleSaveDescriptors = useCallback(() => {
        setLoading(true);
        const descriptorsUpdateSpecs = getDescriptorsUpdateSpec(Object.values(descriptorsMap));

        ImportsRepo.descriptorsUpdate(extraction.id, { descriptorsUpdateSpecs }, (result: any) => {
            if (result.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Fields updated successfully');
                disableDescriptorsEditMode();
                setImportTable(result.data);
                if (hasPendingNormalization) {
                    BrowserStorage.setLabelsNormalizationStatus(extraction.id, true);
                    setHasPendingNormalization(false);
                }
            } else {
                Alert.error(`ERROR: ${result.message}`);
            }
            setLoading(false);
        });
    }, [descriptorsMap, setLoading, disableDescriptorsEditMode, setImportTable, extraction.id]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (loading) {
                return;
            }
            if (labelNormalizationModalState.open) {
                return;
            }
            if (event.key === KEYS.ENTER) {
                if (descriptorsEditMode) {
                    handleSaveDescriptors();
                }
                if (reportsEditMode) {
                    handleSaveReports();
                }
                setCellCoordinates({ activeRow: null, activeCol: null });
                setImagePopupOpen(false);
            } else if (event.key === KEYS.ESCAPE) {
                cancelAllReportsEditing();
                cancelDescriptorsEditing();
                setCellCoordinates({ activeRow: null, activeCol: null });
                setImagePopupOpen(false);
                setHasPendingNormalization(false);
            }
        };

        document.addEventListener(EVENTS.KEY_DOWN, handleKeyDown);
        return () => {
            document.removeEventListener(EVENTS.KEY_DOWN, handleKeyDown);
        };
    }, [
        labelNormalizationModalState.open,
        descriptorsEditMode,
        reportsEditMode,
        handleSaveDescriptors,
        handleSaveReports,
        cancelAllReportsEditing,
        cancelDescriptorsEditing,
        loading,
        setCellCoordinates,
    ]);

    useEffect(() => {
        const handleKeyNavigation = (event: KeyboardEvent) => {
            const isModifierPressed = event.ctrlKey || event.metaKey; // Ctrl on Win/Linux, Command on macOS
            if (activeRow === null || activeCol === null || !isModifierPressed) {
                return;
            }
            if (event.key === KEYS.ARROW_UP) {
                setCellCoordinates({ activeRow: activeRow - 1, activeCol });
            } else if (event.key === KEYS.ARROW_RIGHT) {
                setCellCoordinates({ activeRow, activeCol: activeCol + 1 });
            } else if (event.key === KEYS.ARROW_DOWN) {
                setCellCoordinates({ activeRow: activeRow + 1, activeCol });
            } else if (event.key === KEYS.ARROW_LEFT) {
                setCellCoordinates({ activeRow, activeCol: activeCol - 1 });
            } else if (event.key === KEYS.LOWER_I) {
                setImagePopupOpen((prev) => !prev);
            }
        };

        document.addEventListener(EVENTS.KEY_DOWN, handleKeyNavigation);

        return () => {
            document.removeEventListener(EVENTS.KEY_DOWN, handleKeyNavigation);
        };
    }, [activeRow, activeCol, setCellCoordinates]);

    const handleApplyImport = () => {
        setLoading(true);
        applyModalState.closeModal();
        ImportsRepo.applyImport(extraction.id, (response: any) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Import applied successfully');
                BrowserStorage.deleteLabelsNormalizationStatus(extraction.id);
                redirectBack();
            } else {
                Alert.error(`ERROR: ${response.message}`);
                setLoading(false);
            }
        });
    };

    const confirmDescriptorsLabelsCleanup = (cleanCandidates: DescriptorCleanedLabel[]) => {
        const descriptorsToLabels = cleanCandidates.reduce((acc, curr) => {
            acc[curr.descriptorId] = curr.cleanLabel;
            return acc;
        }, {} as DescriptorsToLabelsMapType);

        setDescriptorsLabels(descriptorsToLabels);
        labelNormalizationModalState.closeModal();
        setHasPendingNormalization(true);
    };

    const confirmDescriptorsLabelsCleanupWithSave = (cleanCandidates: DescriptorCleanedLabel[]) => {
        const descriptorsToLabels = cleanCandidates.reduce((acc, curr) => {
            acc[curr.descriptorId] = curr.cleanLabel;
            return acc;
        }, {} as DescriptorsToLabelsMapType);
        setDescriptorsLabels(descriptorsToLabels);
        setHasPendingNormalization(true);
        setLoading(true);
        const descriptorsUpdateSpecs = getDescriptorsUpdateSpec(
            Object.values(descriptorsMap).map((x) => ({ ...x, label: descriptorsToLabels[x.id] ?? x.label }))
        );

        ImportsRepo.descriptorsUpdate(extraction.id, { descriptorsUpdateSpecs }, (result: any) => {
            if (result.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Fields updated successfully');
                setImportTable(result.data);
                if (hasPendingNormalization) {
                    BrowserStorage.setLabelsNormalizationStatus(extraction.id, true);
                    setHasPendingNormalization(false);
                }
                labelNormalizationWithSave.closeModal();
                handleCheckImpact();
                applyModalState.openModal();
            } else {
                Alert.error(`ERROR: ${result.message}`);
            }
            setLoading(false);
        });
    };

    return (
        <Segment>
            <Segment className="overflow--auto">
                <Table
                    extraction={extraction}
                    imagePopupOpen={imagePopupOpen}
                    recalculate={recalculate}
                    openDescriptorSettings={fieldModalState.openModal}
                />
                {descriptorsEditMode && (
                    <Button className="mt-05" size="mini" primary onClick={labelNormalizationModalState.openModal}>
                        Normalize Labels
                    </Button>
                )}
            </Segment>
            <ButtonGroup fluid widths={3}>
                <Button disabled={loading} primary onClick={handleOpenImpactModal}>
                    Check Impact
                </Button>
                <Button disabled={loading} positive onClick={handleOpenApplyModal}>
                    Apply
                </Button>
                <Button disabled={loading} negative onClick={handleDeleteImport}>
                    Delete
                </Button>
            </ButtonGroup>
            <Legend />
            {applyModalState.open && (
                <ApplyConfirmationModal
                    open={applyModalState.open}
                    closeModal={applyModalState.closeModal}
                    impact={impact}
                    onConfirm={handleApplyImport}
                />
            )}
            {labelNormalizationModalState.open && (
                <LabelNormalizationModal
                    heading="Label Normalization"
                    open={labelNormalizationModalState.open}
                    descriptors={normalizationCandidate}
                    closeModal={labelNormalizationModalState.closeModal}
                    onConfirm={confirmDescriptorsLabelsCleanup}
                />
            )}
            {labelNormalizationWithSave.open && (
                <LabelNormalizationModal
                    heading="Label Normalization Before Apply"
                    open={labelNormalizationWithSave.open}
                    descriptors={normalizationCandidate}
                    closeModal={() => {
                        labelNormalizationWithSave.closeModal();
                        handleCheckImpact();
                        applyModalState.openModal();
                    }}
                    onConfirm={confirmDescriptorsLabelsCleanupWithSave}
                    cancelButtonText="SKIP"
                />
            )}
            {impactModalState.open && (
                <ImpactModal open={impactModalState.open} closeModal={impactModalState.closeModal} impact={impact} />
            )}

            {fieldModalState.open && (
                <FieldModal
                    schemaId={extraction.schemaId}
                    className="import-table-field-modal"
                    formSpec={FORM_SPEC}
                    descriptor={fieldModalState.modalData}
                    descriptors={fieldModalDescriptors}
                    flatDescriptors={fieldModalDescriptors}
                    onCancel={fieldModalState.closeModal}
                    open={fieldModalState.open}
                    onSave={(d) => {
                        updateDescriptor(d.id, { verticalCalculation: d.calculation });
                        fieldModalState.closeModal();
                    }}
                />
            )}
        </Segment>
    );
};

export default ImportTable;

function recalculateTable(
    requestTimeStamp: number,
    importId: number,
    descriptorsMap: DescriptorsMapType,
    reportsMap: ReportsMapType,
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
) {
    const descriptorsUpdateSpecs = getDescriptorsUpdateSpec(Object.values(descriptorsMap));
    const reportsUpdateSpec = Object.values(reportsMap).map((report) => {
        return {
            id: report.id,
            rowIdToValue: Object.entries(reportsMap[report.id].rowIdToValue).reduce(
                (acc: Record<RowIdType, FieldValueUpdateSpec>, [key, value]) => {
                    acc[key] = {
                        value: value.normalizedValue,
                        manuallyDisabledVerticalCalc: value.manuallyDisabledVerticalCalc,
                    };
                    return acc;
                },
                {}
            ),
        };
    });
    const importTable = {
        descriptorUpdates: descriptorsUpdateSpecs,
        reportUpdates: reportsUpdateSpec,
    };

    ImportsRepo.recalculateTable(
        importId,
        importTable,
        (result: any) => {
            successCallback({ ...result, requestTimeStamp });
        },
        (error: any) => {
            errorCallback({ ...error, requestTimeStamp });
        }
    );
}

